import { Box, TextField, Typography } from '@mui/material'
import { MapModule, useBingMaps } from '../../../hooks/use-bingMaps'
import { useCallback, useRef } from 'react'
import type { Address } from '../../../models/Address'
import './bingmap.css'
import { TextFieldWithLabel } from '@oaisd/michdev.components.react'
import { parseAddress } from 'addresser'

interface LocationDetailsProps {
  onChange: (e: any) => void
  onPlaceSelected?: (address: Address) => void
  editData: Address
  onBlur?: (dat: Address) => void
  hidePrimary?: boolean
  integratedLabel?: boolean
  error?: boolean
  required?: boolean
  disabled?: boolean
}

const MODULES = [MapModule.AutoSuggest]

export function LocationSuggestBox (props: LocationDetailsProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const doNothing = useCallback((): void => {}, [])

  const { hasError } = useBingMaps({
    modules: MODULES,
    selectFunction: props.onPlaceSelected ?? doNothing,
    inputRef,
    containerRef,
    baseAddress: props.editData
  })

  const handleBlur = (): void => {
    // If Bing Maps failed and user typed manually, parse the address
    if (hasError && props.editData.street !== '') {
      // We should send to sentry when bing fails
      const parsed = parseAddress(props.editData.street)

      if (parsed !== null) {
        // Create a parsed address object
        const parsedAddress: Address = {
          id: props.editData.id,
          street: parsed.addressLine1,
          city: parsed.placeName,
          state: parsed.stateAbbreviation,
          zip: parsed.zipCode
        }

        if (props.onBlur !== undefined) {
          props.onBlur(parsedAddress)
        } else if (props.onPlaceSelected !== undefined) {
          props.onPlaceSelected(parsedAddress)
        }
        return
      }
    }

    // Call onBlur with current data if provided
    if (props.onBlur !== undefined) {
      props.onBlur(props.editData)
    }
  }

  return (<Box>
    {hasError && (
      <Box sx={{
        backgroundColor: '#f5f5f5',
        padding: '12px',
        borderRadius: '4px',
        border: '1px solid #e0e0e0',
        mb: 1
      }}
      >
        <Typography variant='body2' color='text.primary'>
          Address suggestions are currently unavailable. Please ensure the address is entered correctly.
        </Typography>
      </Box>
    )}
    { props.integratedLabel === true &&
      <TextField
        disabled={props.disabled}
        fullWidth
        variant='filled'
        autoComplete='off'
        id='address'
        name='address'
        label='Address'
        value={props.editData.street ?? ''}
        required={props.required}
        error={props.error}
        onChange={props.onChange}
        minRows={2}
        onBlur={handleBlur}
        inputRef={inputRef}
        sx={{
          border: 'none',
          boxShadow: 'none',
          pt: 0
        }}
        inputProps={{
          shrink: 'false',
          role: 'combobox',
          'aria-expanded': true,
          'aria-controls': 'as_containerSearch_address'
        }}
        data-testid='addressSearch'
      />
    }
    { props.integratedLabel !== true &&
      <TextFieldWithLabel
        disabled={props.disabled}
        ariaRole='combobox'
        ariaExpanded={true}
        ariaControls='as_containerSearch_address'
        label='Address'
        name='address'
        data-testid="address"
        autoComplete={'off'}
        className='pt-0 pb-0'
        textFieldClassName='pb-0'
        onBlur={handleBlur}
        onChange={props.onChange}
        value={props.editData.street ?? ''}
        inputRef={inputRef}
      />
    }
    {!hasError && (
      <div id='searchBoxContainer' ref={containerRef} style={{ width: '100%' }}></div>
    )}
  </Box>)
}
